import { supabase } from '../supabaseClient';
import { resolveForfeoReserveEdgeErrorCode } from './forfeoReserveEdgeError';
import {
  FORFEO_RESERVE_PRODUCTION_SCHEMA,
  normalizeReservePartnerOrigin,
  validateReservePartnerManagementCommand,
  type ForfeoReserveAdminIntegrationSnapshot,
  type ForfeoReserveMerchantIntegrationSnapshot,
  type ForfeoReservePartnerManagementCommand,
  type ForfeoReserveProductionReadinessSnapshot,
} from '../../supabase/functions/_shared/forfeo-reserve-production';

export const FORFEO_RESERVE_INTEGRATIONS_FEATURE_FLAG =
  'VITE_FORFEO_RESERVE_INTEGRATIONS_V1' as const;

export function isForfeoReserveIntegrationsEnabled(): boolean {
  return import.meta.env.VITE_FORFEO_RESERVE_INTEGRATIONS_V1 === 'true';
}

export class ForfeoReserveProductionClientError extends Error {
  readonly code: string;

  constructor(code: string) {
    super(code);
    this.name = 'ForfeoReserveProductionClientError';
    this.code = code;
  }
}

function correlationId(): string {
  return `reserve:production:${crypto.randomUUID()}`;
}

function idempotencyKey(kind: string): string {
  return `reserve:partner:${kind}:${crypto.randomUUID()}`;
}

async function invoke<T>(
  functionName: 'reserve-integration-center' | 'reserve-production-readiness',
  body: Record<string, unknown>,
): Promise<T> {
  const { data, error } = await supabase.functions.invoke(functionName, { body });
  if (error) {
    throw new ForfeoReserveProductionClientError(
      await resolveForfeoReserveEdgeErrorCode(
        data,
        error,
        'reserve_production_request_failed',
      ),
    );
  }

  if (!data?.ok) {
    throw new ForfeoReserveProductionClientError(
      String(data?.error || 'reserve_production_request_failed'),
    );
  }
  return data as T;
}

export async function fetchReserveMerchantIntegrations(
  businessId: string,
): Promise<ForfeoReserveMerchantIntegrationSnapshot> {
  const response = await invoke<{
    ok: true;
    schema: typeof FORFEO_RESERVE_PRODUCTION_SCHEMA;
    snapshot: ForfeoReserveMerchantIntegrationSnapshot;
  }>('reserve-integration-center', {
    action: 'merchant_snapshot',
    businessId,
    correlationId: correlationId(),
  });
  return response.snapshot;
}

export async function fetchReserveAdminIntegrations(): Promise<
  ForfeoReserveAdminIntegrationSnapshot
> {
  const response = await invoke<{
    ok: true;
    snapshot: ForfeoReserveAdminIntegrationSnapshot;
  }>('reserve-integration-center', {
    action: 'admin_snapshot',
    correlationId: correlationId(),
  });
  return response.snapshot;
}

export async function applyReservePartnerCommand(
  input: Omit<
    ForfeoReservePartnerManagementCommand,
    'idempotencyKey' | 'correlationId'
  > & Partial<
    Pick<
      ForfeoReservePartnerManagementCommand,
      'idempotencyKey' | 'correlationId'
    >
  >,
): Promise<Record<string, unknown>> {
  const command: ForfeoReservePartnerManagementCommand = {
    ...input,
    origin: input.origin ? normalizeReservePartnerOrigin(input.origin) : undefined,
    idempotencyKey: input.idempotencyKey || idempotencyKey(input.kind),
    correlationId: input.correlationId || correlationId(),
  };

  const errors = validateReservePartnerManagementCommand(command);
  if (errors.length > 0) {
    throw new ForfeoReserveProductionClientError(errors.join(','));
  }

  const response = await invoke<{
    ok: true;
    result: Record<string, unknown>;
  }>('reserve-integration-center', {
    action: 'command',
    command,
  });
  return response.result;
}

export async function fetchReserveProductionReadiness(): Promise<
  ForfeoReserveProductionReadinessSnapshot
> {
  const response = await invoke<{
    ok: true;
    snapshot: ForfeoReserveProductionReadinessSnapshot;
  }>('reserve-production-readiness', {
    action: 'snapshot',
    correlationId: correlationId(),
  });
  return response.snapshot;
}

export function buildReserveWidgetSnippet(input: {
  publicWidgetKey: string;
  locale?: 'fr' | 'en';
  accentColor?: string | null;
  itemId?: string;
}): string {
  const attributes = [
    'data-forfeo-reserve-widget',
    `data-public-key="${input.publicWidgetKey}"`,
    `data-locale="${input.locale || 'fr'}"`,
    `data-accent="${input.accentColor || '#5b21b6'}"`,
    input.itemId ? `data-item="${input.itemId}"` : '',
  ].filter(Boolean);

  return [
    `<div ${attributes.join(' ')}></div>`,
    '<script src="https://forfeoexplore.com/widgets/reserve.js" ',
    'data-forfeo-reserve-widget async></script>',
  ].join('\n');
}

export function buildReserveWebmasterMailto(input: {
  webmasterEmail: string;
  businessName: string;
  domain: string;
  snippet: string;
}): string {
  const subject = `Intégration Forfeo Reserve — ${input.businessName}`;
  const body = [
    'Bonjour,',
    '',
    `${input.businessName} vous autorise à intégrer son widget Forfeo Reserve.`,
    `Domaine autorisé : ${input.domain}`,
    '',
    'Code à ajouter sur la page de réservation :',
    input.snippet,
    '',
    'Documentation : https://forfeoexplore.com/aide/formation',
    '',
    'Ce message contient uniquement une clé publique de widget.',
    'Aucune clé API privée, clé Stripe ou clé Supabase ne doit être envoyée par courriel.',
  ].join('\n');

  return `mailto:${encodeURIComponent(input.webmasterEmail)}`
    + `?subject=${encodeURIComponent(subject)}`
    + `&body=${encodeURIComponent(body)}`;
}
